import { useState, useEffect, useCallback, useRef } from 'react';
import { useVoiceStreaming } from '../voice/useVoiceStreaming';
import { playRingingOutSound, stopRingingSound } from '../../utils/soundUtils';

// Safe audio creation with error handling
const createSafeAudio = (src) => {
    if (typeof Audio === 'undefined') return null;
    try {
        const audio = new Audio(src);
        audio.preload = 'metadata';
        audio.addEventListener('error', (e) => {
            console.debug(`Audio file not available: ${src}`);
        });
        return audio;
    } catch (error) {
        console.debug(`Failed to create audio element for: ${src}`, error);
        return null;
    }
};

const endCallSound = createSafeAudio('/sounds/end-call.mp3');

/**
 * useStreamingCall - Hook for managing streaming voice calls
 *
 * Wraps useVoiceStreaming to provide a normalized interface that matches
 * the turn-based call hook, enabling shared UI components.
 *
 * @param {Object} options
 * @param {string} options.wsUrl - WebSocket URL for voice streaming
 * @param {string} options.apiBaseUrl - Base URL for API calls
 * @param {Object} options.streamingConfig - Configuration for voice streaming
 * @param {boolean} options.enableSoundEffects - Whether to play sound effects
 * @param {Object} options.activeCallContact - The contact being called
 * @param {boolean} options.isEnvironmentOffline - Whether environment is offline
 * @param {Function} options.onCallEnd - Callback when call ends
 */
export function useStreamingCall({
    wsUrl,
    streamingConfig = {},
    enableSoundEffects = false,
    activeCallContact,
    isEnvironmentOffline = false,
    onCallEnd,
}) {
    // Call state
    const [callStatus, setCallStatus] = useState('idle');
    const [callStartTime, setCallStartTime] = useState(null);
    const [callDuration, setCallDuration] = useState(0);
    const [showKeypad, setShowKeypad] = useState(false);
    const [dialedNumber, setDialedNumber] = useState('');

    // Voice state derived from streaming hook
    const [voiceState, setVoiceState] = useState('idle');

    // Refs
    const callingTimerRef = useRef(null);
    const hasStartedStreamingRef = useRef(false);

    // Callbacks for voice streaming events
    const handleSpeechStart = useCallback(() => {
        if (callStatus === 'Calling...') {
            console.log('Speech started, connecting call...');
            setCallStatus('Connected');
            setCallStartTime(Date.now());
            stopRingingSound();
            setShowKeypad(true);
        }
        setVoiceState('speaking');
    }, [callStatus]);

    const handleSpeechEnd = useCallback(() => {
        // After speaking ends, go back to listening
        setVoiceState('listening');
    }, []);

    // Use voice streaming hook
    const {
        isStreaming,
        waitingForAssistant,
        waitingForUser,
        startStreaming,
        stopStreaming,
    } = useVoiceStreaming({
        wsUrl,
        config: streamingConfig,
        onSpeechStart: handleSpeechStart,
        onSpeechEnd: handleSpeechEnd
    });

    // Map streaming states to voiceState
    useEffect(() => {
        if (!isStreaming) {
            setVoiceState('idle');
        } else if (waitingForUser) {
            setVoiceState('listening');
        } else if (waitingForAssistant) {
            setVoiceState('processing');
        }
    }, [isStreaming, waitingForUser, waitingForAssistant]);

    // Call duration timer
    useEffect(() => {
        let interval;
        if (callStatus === 'Connected' && callStartTime) {
            const updateDuration = () => {
                const now = Date.now();
                const durationInSeconds = Math.floor((now - callStartTime) / 1000);
                setCallDuration(durationInSeconds);
            };
            updateDuration();
            interval = setInterval(updateDuration, 1000);
        }
        return () => clearInterval(interval);
    }, [callStatus, callStartTime]);

    // Start call - plays ringing and starts streaming, waits for audioReady to connect
    const startCall = useCallback(async () => {
        if (callStatus !== 'idle') return;

        console.log('Starting streaming call...');
        setCallStatus('Calling...');
        playRingingOutSound(enableSoundEffects, isEnvironmentOffline);

        // Start streaming immediately - audioReady event will trigger connection
        if (!hasStartedStreamingRef.current) {
            hasStartedStreamingRef.current = true;
            try {
                await startStreaming();
                console.log('Streaming started successfully');
            } catch (error) {
                console.error('Failed to start streaming:', error);
                hasStartedStreamingRef.current = false;
                stopRingingSound();
                setCallStatus('idle');
            }
        }
    }, [callStatus, enableSoundEffects, isEnvironmentOffline, startStreaming]);

    // End call
    const handleEndCall = useCallback(() => {
        console.log('Ending streaming call...');

        // Clear any pending timers
        if (callingTimerRef.current) {
            clearTimeout(callingTimerRef.current);
            callingTimerRef.current = null;
        }

        // Play end call sound
        if (enableSoundEffects && endCallSound) {
            endCallSound.currentTime = 0;
            endCallSound.play().catch(e => console.debug('End call sound play failed:', e));
        }

        // Stop ringing if still playing
        stopRingingSound();

        // Stop streaming
        if (isStreaming) {
            stopStreaming();
        }

        // Reset state
        hasStartedStreamingRef.current = false;
        setCallStatus('idle');
        setCallStartTime(null);
        setCallDuration(0);
        setShowKeypad(false);
        setDialedNumber('');
        setVoiceState('idle');

        // Notify parent
        onCallEnd?.();

        console.log('Streaming call ended');
    }, [enableSoundEffects, isStreaming, stopStreaming, onCallEnd]);

    // Keypad press handler
    const handleKeypadPress = useCallback((number) => {
        setDialedNumber(prev => prev + number);
    }, []);

    // Auto-start call when contact is set
    useEffect(() => {
        if (activeCallContact && callStatus === 'idle') {
            startCall();
        }
    }, [activeCallContact, callStatus, startCall]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (callingTimerRef.current) {
                clearTimeout(callingTimerRef.current);
            }
            if (isStreaming) {
                stopStreaming();
            }
            stopRingingSound();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        // Call state
        callStatus,
        callDuration,
        showKeypad,
        dialedNumber,

        // Voice state
        voiceState,
        needsUserTap: false, // Streaming doesn't need iOS tap workaround
        isStreaming,

        // Derived
        showAIControls: callStatus === 'Connected',

        // Actions
        startCall,
        handleEndCall,
        handleKeypadPress,
        handleUserTap: () => { }, // No-op for streaming
    };
}

export default useStreamingCall;
